import {
  FetchError,
  ProblemDetailError,
  ApiError as SdkApiError
} from "@wingspanhq/wingspan-api-sdk";

export type ApiError = SdkApiError;

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof SdkApiError;

/**
 * The v1 users-service error shape: an axios error whose `response` carries a
 * parsed JSON body. v3 SDK errors also have a `response`, but it is a fetch
 * `Response` with no `data`, which is what separates the two.
 */
export type LegacyApiError = {
  response: {
    status?: number;
    data: {
      error?: string;
      message?: string;
      type?: string;
      errorType?: string;
      subtype?: string;
      errorSubType?: string;
    };
  };
};

export const isLegacyApiError = (error: unknown): error is LegacyApiError =>
  !isApiError(error) && !!(error as any)?.response?.data;

export const isNetworkError = (error: unknown): boolean =>
  error instanceof FetchError ||
  (error instanceof Error &&
    (error.name === "FetchError" || error.message === "Network Error"));

/** HTTP status of a failed call — SDK errors first, legacy axios shape as fallback. */
export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (isApiError(error)) {
    return error.status;
  }
  return (error as any)?.response?.status;
};

/** A `409 Conflict` — the resource already exists, or the transition it asks for is taken. */
export const isConflictError = (error: unknown): boolean =>
  getApiErrorStatus(error) === 409;

/** Human-readable message of a failed call, per RFC 9457 precedence. */
export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ProblemDetailError) {
    return error.detail || error.title || error.response.statusText;
  }
  if (isApiError(error)) {
    return error.response.statusText;
  }
  if (isLegacyApiError(error)) {
    return error.response.data.error || error.response.data.message;
  }
  return undefined;
};

/**
 * The v1 `type`/`subtype` pair (also spelled `errorType`/`errorSubType`), which
 * is the legacy analogue of a v3 problem's `code`/`detailCode`.
 */
export const getLegacyApiErrorCodes = (
  error: LegacyApiError
): { code: string | undefined; detailCode: string | undefined } => {
  const { data } = error.response;
  return {
    code: data.type || data.errorType,
    detailCode: data.subtype || data.errorSubType
  };
};
